/// <amd-module name="Wiki/NoWiki/WikiZeroDay" />
import * as React from "react";

import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Tooltip } from "VSSUI/TooltipEx";
import { ZeroData } from "VSSUI/ZeroData";

import { WikiCreateParametersV2, WikiType, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { getWikiClient } from "Wiki/Clients/Wiki/RestClient/Wiki";
import * as Resources from "Wiki/NoWiki/Resources";
import { UrlConstants } from "Wiki/ViewCommon/Constants";
import { TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { getVersionFromBranchName, navigateToUrl, publish } from "Wiki/ViewCommon/Utils/WikiHelper";
import { LoadingSpinner, showWrappedDialog } from "Wiki/ViewCommon/WrappedDialogHelper";

export interface WikiZeroDayProps {
    hasWikiCreatePermission: boolean;
    hasProjectContributePermission?: boolean;
    projectId: string;
    isTFVCOnlyProject?: boolean;
}

export interface WikiZeroDayState {
    wikiCreateErrorMessage?: string;
    isActionInProgress?: boolean;
}

export class WikiZeroDay extends VssComponent<WikiZeroDayProps, WikiZeroDayState> {
    constructor(props: WikiZeroDayProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};
    }

    public componentDidMount(): void {
        super.componentDidMount();
        publish(this.context.pageContext, TelemetryConstants.WikiCreateScreen, undefined, {
            [TelemetryConstants.LandingPageInSufficientCreateRepoPermissionScreen]: !this.props.hasWikiCreatePermission,
            [TelemetryConstants.LandingPageInSufficientWritePermissionScreen]: !this.props.hasProjectContributePermission,
            [TelemetryConstants.IsTFVCProject]: this.props.isTFVCOnlyProject
        });
    }

    public render(): JSX.Element {
        return (
            <>
                <div className="flex-column flex-grow">
                    {!!this.state.wikiCreateErrorMessage && (
                        <MessageCard className="flex-self-stretch" onDismiss={this._onDismiss} severity={MessageCardSeverity.Error}>
                            {this.state.wikiCreateErrorMessage}
                        </MessageCard>
                    )}
                    <div className="wiki-nowiki flex-column justify-center">
                        <ZeroData
                            primaryText={Resources.ZeroDay_PrimaryText}
                            secondaryText={Resources.ZeroDay_SecondaryText}
                            imagePath={getAssetLocation(this.context.pageContext, "ms.vss-wiki-web/wiki-nowiki-experience/Images/wiki-zeroday.svg")}
                            imageAltText=""
                        />
                        <div className="flex-column flex-center">
                            <div className="flex-row">
                                <Button
                                    className="create-wiki"
                                    text={Resources.ZeroDay_CreateWikiCTAText}
                                    primary={true}
                                    onClick={this._onCreateWikiClick}
                                    disabled={!this.props.hasWikiCreatePermission || this.state.isActionInProgress}
                                />

                                {!this.props.isTFVCOnlyProject && (
                                    <Button
                                        className="publish-wiki"
                                        text={Resources.ZeroDay_PublishCodeAsWiki}
                                        onClick={this._onPublishWiki}
                                        disabled={!this.props.hasProjectContributePermission || this.state.isActionInProgress}
                                    />
                                )}
                            </div>
                            <Tooltip text={Resources.ZeroDay_LearnMoreHelp}>
                                <Link className="wiki-learn-more" href={Resources.ZeroDay_LearnMoreLink} rel="noopener noreferrer" target="_blank">
                                    {Resources.ZeroDay_LearnMoreCTAText}
                                </Link>
                            </Tooltip>
                        </div>
                    </div>
                </div>
                {this.state.isActionInProgress && <LoadingSpinner />}
            </>
        );
    }

    private _onDismiss = (): void => {
        this.setState({ wikiCreateErrorMessage: undefined });
    };

    private _onPublishWiki = (): void => {
        showWrappedDialog(this.context.pageContext, {}, "Wiki.PublishCodeWikiPanel", ["ms.vss-wiki-web.publish-codewiki-panel-content"]);
        // TODO: add telemetry
    };

    private _onCreateWikiClick = (): void => {
        this.setState({ isActionInProgress: true, wikiCreateErrorMessage: undefined });
        // TODO: add telemetry
        this.trackPromise(
            getWikiClient(this.context.pageContext).createWiki({
                type: WikiType.ProjectWiki,
                projectId: this.props.projectId
            } as WikiCreateParametersV2)
        ).promise.then(
            (wiki: WikiV2) => {
                publish(this.context.pageContext, TelemetryConstants.WikiCreated, TelemetryConstants.WikiTypeProject);
                navigateToUrl(this.context.pageContext, wiki.projectId, wiki.name, {
                    [UrlConstants.WikiVersionParam]: getVersionFromBranchName(wiki.versions[0].version)
                });
            },
            (error: Error) => {
                this.setState({ wikiCreateErrorMessage: error.message, isActionInProgress: false });
            }
        );
    };
}
